import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Save, Users, Camera, Upload, X, ScanFace, RefreshCw } from "lucide-react";
import * as faceapi from "@vladmandic/face-api";
import AppShell, { useAppUser } from "@/components/AppShell";
import BeltBar from "@/components/BeltBar";
import UserAvatar from "@/components/UserAvatar";
import { authApi, formatApiError } from "@/lib/api";
import { imageToDataUrl, censorFaces } from "@/lib/files";

const inputCls =
  "w-full bg-obsidian border border-white/10 focus:border-gold/60 rounded-xl px-4 py-3 text-sm text-[#F5F5F0] placeholder:text-zinc-600 outline-none transition-colors duration-200";

const sessionKey = (s) => `${s.day}|${s.start}|${s.class_name || ""}`;
const FACE_THRESHOLD = 0.55;

let modelsPromise = null;
const loadFaceModels = () => {
  if (!modelsPromise) {
    const loads = Promise.all([
      faceapi.nets.ssdMobilenetv1.loadFromUri("/models"),
      faceapi.nets.faceLandmark68Net.loadFromUri("/models"),
      faceapi.nets.faceRecognitionNet.loadFromUri("/models"),
    ]);
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("timeout-modelos")), 60000)
    );
    modelsPromise = Promise.race([loads, timeout]).catch((e) => {
      modelsPromise = null;
      throw e;
    });
  }
  return modelsPromise;
};

export default function Presenca() {
  const { user } = useAppUser() || {};
  const [units, setUnits] = useState(null);
  const [unitId, setUnitId] = useState("");
  const [session, setSession] = useState(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [alunos, setAlunos] = useState([]);
  const [present, setPresent] = useState(new Set());
  const [resumo, setResumo] = useState(null);
  const [saving, setSaving] = useState(false);

  const [faceOpen, setFaceOpen] = useState(false);
  const [photoData, setPhotoData] = useState(null);
  const [chamadaPhoto, setChamadaPhoto] = useState(null);
  const [censorUnknown, setCensorUnknown] = useState(true);
  const [cameraOn, setCameraOn] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    authApi().get("/units").then((r) => setUnits(r.data.units || [])).catch(() => setUnits([]));
  }, [user]);

  useEffect(() => () => stopCamera(), []);

  const unit = (units || []).find((u) => u.id === unitId);
  const sessions = unit ? unit.schedule || [] : [];

  const loadAlunos = async (uid) => {
    try {
      const [ra, rr] = await Promise.all([
        authApi().get("/alunos", { params: { unit_id: uid, com_foto: true } }),
        authApi().get("/presenca/resumo", { params: { unit_id: uid } }),
      ]);
      setAlunos(ra.data.alunos);
      setResumo(rr.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const loadExisting = async (uid, s, d) => {
    try {
      const r = await authApi().get("/presenca", { params: { unit_id: uid, date: d, day: s.day, start: s.start } });
      setPresent(new Set(r.data.attendance?.present_emails || []));
    } catch {
      setPresent(new Set());
    }
  };

  const pickUnit = (uid) => {
    setUnitId(uid);
    setSession(null);
    setPresent(new Set());
    setChamadaPhoto(null);
    if (uid) loadAlunos(uid);
  };

  const pickSession = (s) => {
    setSession(s);
    setChamadaPhoto(null);
    loadExisting(unitId, s, date);
  };

  const pickDate = (d) => {
    setDate(d);
    setChamadaPhoto(null);
    if (session) loadExisting(unitId, session, d);
  };

  const toggle = (email) =>
    setPresent((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });

  const removerAusencia = async (aluno) => {
    try {
      await authApi().post(`/users/${encodeURIComponent(aluno.email)}/ausencia/remover`);
      toast.success(`Pendência de ausência removida de ${aluno.name}.`);
      setAlunos((prev) => prev.map((x) => (x.email === aluno.email ? { ...x, ausencia_pendente: false, faltas_seguidas: 0 } : x)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const save = async () => {
    if (!unitId || !session || !date) {
      toast.error("Selecione a unidade, o treino e a data.");
      return;
    }
    setSaving(true);
    try {
      let photo = null;
      if (chamadaPhoto) {
        photo = await censorFaces(chamadaPhoto.image, chamadaPhoto.faces);
      }
      const r = await authApi().post("/presenca", {
        unit_id: unitId,
        date,
        day: session.day,
        class_name: session.class_name || null,
        start: session.start,
        end: session.end,
        present_emails: [...present],
        ...(photo ? { photo } : {}),
      });
      toast.success(photo ? "Chamada salva e foto registrada na Galeria (rostos de menores borrados)." : `Chamada salva: ${r.data.presentes} presente(s).`);
      const rr = await authApi().get("/presenca/resumo", { params: { unit_id: unitId } });
      setResumo(rr.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // --- Chamada por foto ---

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOn(false);
  };

  const openFaceModal = () => {
    setPhotoData(null);
    setFaceOpen(true);
  };

  const closeFaceModal = () => {
    stopCamera();
    setFaceOpen(false);
    setPhotoData(null);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      setCameraOn(true);
      requestAnimationFrame(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      });
    } catch {
      toast.error("Não foi possível acessar a câmera. Envie uma imagem.");
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    setPhotoData(canvas.toDataURL("image/jpeg", 0.9));
    stopCamera();
  };

  const pickPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setPhotoData(await imageToDataUrl(file, 1600, 0.85));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const analyzePhoto = async () => {
    if (!photoData) return;
    setAnalyzing(true);
    try {
      const preferredBackend = window.localStorage.getItem("face_backend");
      if (preferredBackend && faceapi.tf?.setBackend) {
        try {
          await faceapi.tf.setBackend(preferredBackend);
          await faceapi.tf.ready();
          console.log("[face] backend forçado:", preferredBackend);
        } catch (e) {
          console.warn("[face] backend", preferredBackend, "falhou:", e);
        }
      }
      await loadFaceModels();
      console.log("[face] modelos carregados, backend:", faceapi.tf?.getBackend?.());
      const img = await faceapi.fetchImage(photoData);
      const detections = await faceapi
        .detectAllFaces(img, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.4 }))
        .withFaceLandmarks()
        .withFaceDescriptors();
      console.log("[face] rostos detectados:", detections.length);
      if (detections.length === 0) {
        toast.error("Nenhum rosto detectado na imagem. Tente outra foto.");
        return;
      }
      const refs = [];
      for (const m of alunos.filter((a) => a.photo)) {
        try {
          const refImg = await faceapi.fetchImage(m.photo);
          const d = await faceapi.detectSingleFace(refImg).withFaceLandmarks().withFaceDescriptor();
          if (d) refs.push({ email: m.email, name: m.name, descriptor: d.descriptor });
        } catch (e) {
          console.warn("[face] falha ao processar foto de perfil de", m.email, e);
        }
      }
      console.log("[face] referencias com rosto:", refs.length);
      if (refs.length === 0) {
        toast.error("Nenhum usuário da unidade tem foto de perfil para comparação.");
        return;
      }
      const matched = new Map();
      const faces = [];
      for (const det of detections) {
        let best = null;
        let bestDist = 1;
        for (const r of refs) {
          const dist = faceapi.euclideanDistance(det.descriptor, r.descriptor);
          if (dist < bestDist) {
            bestDist = dist;
            best = r;
          }
        }
        const isMatch = best && bestDist < FACE_THRESHOLD;
        if (isMatch && (!matched.has(best.email) || matched.get(best.email) > bestDist)) {
          matched.set(best.email, bestDist);
        }
        const aluno = isMatch ? alunos.find((a) => a.email === best.email) : null;
        const box = det.detection.box;
        const blur = isMatch ? !!aluno?.censura : censorUnknown;
        faces.push({ x: box.x, y: box.y, width: box.width, height: box.height, blur });
      }
      setChamadaPhoto({ image: photoData, faces });
      console.log("[face] reconhecidos:", [...matched.entries()], "| rostos a borrar:", faces.filter((f) => f.blur).length);
      setPresent((prev) => new Set([...prev, ...matched.keys()]));
      const names = [...matched.values()].length;
      if (matched.size > 0) {
        toast.success(`${detections.length} rosto(s) na foto · ${matched.size} reconhecido(s) e marcado(s): ${[...matched.keys()].map((em) => alunos.find((a) => a.email === em)?.name || em).join(", ")}. Revise e salve.`);
      } else {
        toast.warning(`${detections.length} rosto(s) detectado(s), mas nenhum correspondeu às fotos de perfil da unidade. Marque manualmente.`);
      }
      closeFaceModal();
    } catch (err) {
      console.error("[face] erro na análise:", err);
      if (err?.message === "timeout-modelos") {
        toast.error("O carregamento do reconhecimento demorou demais. Verifique sua conexão e tente novamente.");
      } else {
        toast.error("Falha na análise da imagem. Tente novamente.");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const comFoto = alunos.filter((a) => a.photo).length;

  return (
    <AppShell active="presenca">
      <section data-testid="presenca-page">
        <h1 className="font-display text-2xl font-bold" data-testid="presenca-title">Presença</h1>
        <p className="font-mono text-xs tracking-[0.3em] text-gold mt-2 mb-6">CHAMADA POR HORÁRIO DE CADA UNIDADE</p>

        <div className="bg-surface border border-white/10 rounded-2xl p-6 space-y-5" data-testid="presenca-setup">
          <select value={unitId} onChange={(e) => pickUnit(e.target.value)} className={inputCls} data-testid="presenca-unit-select">
            <option value="">Selecione a unidade...</option>
            {(units || []).map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>

          {unitId && (
            <div>
              <p className="font-mono text-[11px] tracking-widest text-zinc-500 mb-2">DATA DO TREINO</p>
              <input type="date" value={date} onChange={(e) => pickDate(e.target.value)} className={`${inputCls} [color-scheme:dark] max-w-xs`} data-testid="presenca-date-input" />
            </div>
          )}

          {unitId && sessions.length === 0 && (
            <p className="text-sm text-zinc-500" data-testid="presenca-no-sessions">Esta unidade não tem horários cadastrados.</p>
          )}

          {sessions.length > 0 && (
            <div>
              <p className="font-mono text-[11px] tracking-widest text-zinc-500 mb-2">TREINO</p>
              <div className="grid sm:grid-cols-2 gap-2.5" data-testid="presenca-sessions">
                {sessions.map((s) => (
                  <button
                    key={sessionKey(s)}
                    type="button"
                    onClick={() => pickSession(s)}
                    data-testid={`presenca-session-${s.day}-${s.start.replace(":", "")}`}
                    className={`text-left rounded-xl border px-4 py-3.5 transition-colors ${session && sessionKey(session) === sessionKey(s) ? "border-gold bg-gold/15" : "border-white/15 bg-obsidian hover:border-gold/40"}`}
                  >
                    <p className={`font-display font-semibold ${session && sessionKey(session) === sessionKey(s) ? "text-gold" : "text-[#F5F5F0]"}`} data-testid={`presenca-session-name-${s.day}-${s.start.replace(":", "")}`}>
                      {s.class_name || "Treino"}
                    </p>
                    <p className="font-mono text-[11px] tracking-wider text-zinc-500 mt-1">
                      {s.day.toUpperCase()} · {s.start}–{s.end}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {session && (
          <div className="mt-6 bg-[#1C1C23] border border-white/10 rounded-2xl p-6" data-testid="presenca-list-card">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <div>
                <p className="font-mono text-xs tracking-[0.3em] text-gold" data-testid="presenca-list-label">
                  {(session.class_name || "Treino").toUpperCase()} · {session.day.toUpperCase()} {session.start}–{session.end}
                </p>
                <p className="font-mono text-[11px] text-zinc-500 mt-1.5" data-testid="presenca-list-meta">
                  {date.split("-").reverse().join("/")} · {present.size} PRESENTE(S)
                  {resumo ? ` · ${resumo.total_aulas} CHAMADAS NA UNIDADE` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setCensorUnknown((v) => !v)}
                  data-testid="presenca-censor-unknown-toggle"
                  aria-pressed={censorUnknown}
                  title="Se SIM, rostos de pessoas não cadastradas/reconhecidas também serão borrados"
                  className={`font-mono text-[10px] tracking-wider px-3 py-2 rounded-full border transition-colors ${censorUnknown ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-zinc-500 hover:border-gold/40"}`}
                >
                  CENSURAR DESCONHECIDOS: {censorUnknown ? "SIM" : "NÃO"}
                </button>
                <button
                  type="button"
                  onClick={openFaceModal}
                  data-testid="presenca-face-button"
                  className="inline-flex items-center gap-2 font-display font-semibold text-sm border border-gold/50 text-gold px-5 py-2.5 rounded-full hover:bg-gold hover:text-obsidian transition-colors"
                  title={comFoto === 0 ? "Nenhum usuário da unidade tem foto de perfil" : `${comFoto} usuário(s) com foto para reconhecimento`}
                >
                  <ScanFace size={16} /> Chamada por Foto
                </button>
              </div>
            </div>

            {alunos.length === 0 ? (
              <div className="flex flex-col items-center gap-3 border border-dashed border-white/15 rounded-2xl py-12 text-zinc-500" data-testid="presenca-empty">
                <Users size={28} />
                <p className="text-sm">Nenhum aluno vinculado a esta unidade.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {alunos.map((a, i) => (
                  <div
                    key={a.email}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggle(a.email)}
                    onKeyDown={(e) => e.key === "Enter" && toggle(a.email)}
                    data-testid={`presenca-aluno-${i}`}
                    className={`w-full flex items-center gap-4 rounded-xl border px-4 py-3 text-left transition-colors cursor-pointer ${present.has(a.email) ? "border-gold/60 bg-gold/10" : "border-white/10 bg-obsidian hover:border-white/25"}`}
                  >
                    <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${present.has(a.email) ? "border-gold bg-gold text-obsidian" : "border-white/25"}`}>
                      {present.has(a.email) && <ClipboardCheck size={13} />}
                    </span>
                    <UserAvatar name={a.name} photo={a.photo} birthDate={a.birth_date} size="sm" testid={`presenca-avatar-${i}`} />
                    <span className="flex-1 min-w-0">
                      <span className="font-medium text-sm truncate block">{a.name}</span>
                      {a.ausencia_pendente && (
                        <span className="mt-1 inline-flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[10px] tracking-wider px-2 py-0.5 rounded-full border border-orange-500/60 text-orange-400 bg-orange-500/10" data-testid={`presenca-ausencia-${i}`}>
                            AUSÊNCIA FREQUENTE
                          </span>
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); removerAusencia(a); }}
                            onKeyDown={(e) => e.stopPropagation()}
                            data-testid={`presenca-ausencia-remover-${i}`}
                            className="font-mono text-[10px] tracking-wider px-2 py-0.5 rounded-full border border-gold/40 text-gold hover:bg-gold/15 transition-colors cursor-pointer"
                          >
                            REMOVER PENDÊNCIA
                          </span>
                        </span>
                      )}
                    </span>
                    <BeltBar belt={a.belt || "branca"} degree={a.degree || 0} size="sm" />
                    <span className="font-mono text-[11px] text-zinc-500 shrink-0" data-testid={`presenca-count-${i}`}>
                      {resumo?.por_aluno?.[a.email] || 0} aula(s)
                    </span>
                  </div>
                ))}
              </div>
            )}

            {chamadaPhoto && (
              <div className="mt-5 flex items-center gap-3 border border-gold/30 bg-gold/5 rounded-xl px-4 py-3" data-testid="presenca-photo-attached">
                <img src={chamadaPhoto.image} alt="Foto da chamada" className="w-14 h-14 object-cover rounded-lg border border-white/10" />
                <p className="text-xs text-zinc-400 flex-1 leading-relaxed">
                  Foto anexada à chamada. Ao salvar, <span className="text-gold">{chamadaPhoto.faces.filter((f) => f.blur).length} rosto(s)</span> serão borrados na Galeria (conforme a censura de cada cadastro{''}{censorUnknown ? " + desconhecidos" : ""}).
                </p>
                <button type="button" onClick={() => setChamadaPhoto(null)} data-testid="presenca-photo-remove" className="text-zinc-500 hover:text-crimson transition-colors" aria-label="Remover foto da chamada">
                  <X size={16} />
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={save}
              disabled={saving || alunos.length === 0}
              data-testid="presenca-save-button"
              className="mt-6 inline-flex items-center gap-2 font-display font-semibold bg-gold text-obsidian px-8 py-3.5 rounded-full hover:bg-gold-amber disabled:opacity-60 transition-colors"
            >
              <Save size={16} /> {saving ? "Salvando..." : "Salvar Chamada"}
            </button>
          </div>
        )}
      </section>

      {faceOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="face-modal">
          <div className="absolute inset-0 bg-black/70" onClick={closeFaceModal} />
          <div className="relative bg-[#0D0D10] border border-white/10 rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <p className="font-display font-bold flex items-center gap-2">
                <ScanFace size={18} className="text-gold" /> Chamada por Foto
              </p>
              <button type="button" onClick={closeFaceModal} data-testid="face-modal-close" className="text-zinc-400 hover:text-gold transition-colors" aria-label="Fechar">
                <X size={20} />
              </button>
            </div>

            {!photoData && !cameraOn && (
              <div className="grid grid-cols-2 gap-3" data-testid="face-options">
                <button
                  type="button"
                  onClick={startCamera}
                  data-testid="face-camera-button"
                  className="flex flex-col items-center gap-3 border border-white/15 hover:border-gold/50 rounded-xl py-8 transition-colors"
                >
                  <Camera size={26} className="text-gold" />
                  <span className="font-display font-semibold text-sm">Abrir Câmera</span>
                  <span className="font-mono text-[10px] text-zinc-500">CELULAR OU NOTEBOOK</span>
                </button>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="face-upload-button"
                  className="flex flex-col items-center gap-3 border border-white/15 hover:border-gold/50 rounded-xl py-8 transition-colors"
                >
                  <Upload size={26} className="text-gold" />
                  <span className="font-display font-semibold text-sm">Enviar Imagem</span>
                  <span className="font-mono text-[10px] text-zinc-500">FOTO DA TURMA</span>
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickPhotoFile} data-testid="face-file-input" />
              </div>
            )}

            {cameraOn && (
              <div data-testid="face-camera-view">
                <video ref={videoRef} autoPlay playsInline className="w-full rounded-xl border border-white/10 bg-black" />
                <div className="flex gap-3 mt-4">
                  <button
                    type="button"
                    onClick={capturePhoto}
                    data-testid="face-capture-button"
                    className="flex-1 inline-flex items-center justify-center gap-2 font-display font-semibold bg-gold text-obsidian px-6 py-3 rounded-full hover:bg-gold-amber transition-colors"
                  >
                    <Camera size={16} /> Capturar
                  </button>
                  <button type="button" onClick={stopCamera} data-testid="face-camera-cancel" className="font-mono text-xs tracking-widest border border-white/15 text-zinc-400 px-6 py-3 rounded-full hover:border-crimson/60 hover:text-crimson transition-colors">
                    VOLTAR
                  </button>
                </div>
              </div>
            )}

            {photoData && (
              <div data-testid="face-preview">
                <img src={photoData} alt="Foto da turma" className="w-full rounded-xl border border-white/10" />
                <div className="flex gap-3 mt-4">
                  <button
                    type="button"
                    onClick={analyzePhoto}
                    disabled={analyzing}
                    data-testid="face-analyze-button"
                    className="flex-1 inline-flex items-center justify-center gap-2 font-display font-semibold bg-gold text-obsidian px-6 py-3 rounded-full hover:bg-gold-amber disabled:opacity-60 transition-colors"
                  >
                    <ScanFace size={16} /> {analyzing ? "Analisando rostos..." : "Analisar e Sugerir Presenças"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPhotoData(null)}
                    disabled={analyzing}
                    data-testid="face-retake-button"
                    className="inline-flex items-center gap-2 font-mono text-xs tracking-widest border border-white/15 text-zinc-400 px-5 py-3 rounded-full hover:border-gold/50 hover:text-gold transition-colors"
                  >
                    <RefreshCw size={13} /> TROCAR
                  </button>
                </div>
                <p className="font-mono text-[10px] text-zinc-600 mt-3 leading-relaxed">
                  A análise compara os rostos da foto com as fotos de perfil dos usuários da unidade e sugere as marcações. Revise e ajuste manualmente antes de salvar.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}
